/**
 * Prepares one of this app's own exported HTML files (a Teams transcript, a
 * OneNote page) for display *inside* the app rather than in a new tab.
 *
 * This is the piece that makes bug-free browsing possible where the old
 * open-in-new-tab approach could not: instead of navigating the top frame to
 * a blob: URL (which Chromium blocks when the source document is itself a
 * blob — "about:blank#blocked"), the body is injected into a live element and
 * each relative `<img src>` is rewritten to a blob: URL that loads as a
 * sub-resource. Loading an image from a blob URL is always allowed; only
 * top-level navigation to one is restricted. Relative `<a href>` links are
 * stripped so a stray click can't navigate the whole app away.
 *
 * Pure and DOM-free (regex over the known, self-generated markup) so it stays
 * unit-testable under plain Node, the same split the module render passes use.
 * The content was already sanitized at export time (sanitizeMessageBody /
 * sanitizePage), and injecting via innerHTML never executes <script>, so no
 * second sanitize pass is needed here.
 */

const BODY_RE = /<body[^>]*>([\s\S]*?)<\/body>/i;
const ATTR_RE = /\b(src|href)\s*=\s*"([^"]*)"/gi;

/** A path with no scheme, protocol-relative prefix, root slash, fragment, or blob/data URI. */
export function isRelativeRef(value: string): boolean {
    return value.length > 0 && !/^([a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(value);
}

/** The inner HTML of `<body>`, or the whole string if there is no body element. */
export function extractBody(html: string): string {
    const match = html.match(BODY_RE);
    return match ? (match[1] ?? "") : html;
}

/**
 * Returns the body HTML with every relative image src replaced by whatever
 * `resolveAsset` maps it to (a blob: URL), and every relative link neutralised.
 * An image whose asset can't be resolved has its src dropped rather than left
 * pointing at a path that would 404 against the app's own origin.
 */
export async function inlineHtmlAssets(
    html: string,
    resolveAsset: (relativePath: string) => Promise<string | null>
): Promise<string> {
    const body = extractBody(html);

    const relativeSrcs = new Set<string>();
    for (const match of body.matchAll(ATTR_RE)) {
        const attr = (match[1] ?? "").toLowerCase();
        const value = match[2] ?? "";
        if (attr === "src" && isRelativeRef(value)) {
            relativeSrcs.add(value);
        }
    }

    const resolved = new Map<string, string>();
    for (const rel of relativeSrcs) {
        const url = await resolveAsset(rel);
        if (url) {
            resolved.set(rel, url);
        }
    }

    return body.replace(ATTR_RE, (full, rawAttr: string, value: string) => {
        const attr = rawAttr.toLowerCase();
        if (attr === "src") {
            if (!isRelativeRef(value)) {
                return full;
            }
            const url = resolved.get(value);
            return url ? `src="${url}"` : "";
        }
        // href: strip relative navigation, leave absolute links for the
        // viewer's own click handler to open in a new tab.
        return isRelativeRef(value) ? "" : full;
    });
}
